// The ONE typed API client for the BI SPA. Every fetch lives here
// (`send()`/`request()`/`ApiError`); `@bi/shared` has no API client.
//
// Requests are same-origin in cloud (see `BI_API_BASE` in config.ts) and ride
// `credentials: "include"` for the `bi_authority` cookie.
//
// Identity: the stub `X-Employee-Email` header (local only) + the signed
// `bi_authority` cookie in cloud (`require_bi_employee`, `web/deps/identity.py`).
// 401 = not signed in; 403 = signed in but not an employee, or no `bi_site`
// grant at the level the route needs.

import { BI_API_BASE, EMPLOYEE_EMAIL } from "../config";
import { clearSignedOut, storeEmployeeEmail } from "./identity";
import { activePreviewCatalog, withCatalog } from "./preview-catalog";
import type { ArtifactSummary } from "@bi/shared/types";
import type {
  AskAccepted,
  AskFeedbackAck,
  AskFeedbackRequest,
  AskRequest,
  AskThreadList,
  AskThreadView,
  BiChartResultResponse,
  BiDashboardDetailResponse,
  BiDashboardListResponse,
  BiQueryRequest,
  BiQueryResponse,
  BiSearchResponse,
  BiSourceDetailResponse,
  BiSourceListResponse,
  BiSourceValuesResponse,
  BiSyncAck,
  CorpusBatch,
  CorpusUpload,
  CorpusUploadAck,
  ExperimentListResponse,
  GrantAck,
  GrantListResponse,
  GrantRequest,
  OnlineSchemaInfo,
  ProjectTag,
  PusherAuthResponse,
  RunCompare,
  SchemaDiff,
  TagCount,
  TagDraft,
  TagDraftCreate,
  TagDraftUpdate,
  TagDef,
  TagDefCreate,
  TagDefUpdate,
  TagFinding,
  TaggerInfo,
  TaggingIdentity,
  TagRunAck,
  TagRunInput,
  TagRunRequest,
  TagRunRow,
  TagSet,
  TagSetDiff,
  TagSetImport,
  StudioDashboardCreate,
  StudioDashboardListResponse,
  StudioDashboardResponse,
  StudioDashboardUpdate,
  StudioExportResponse,
  StudioForkRequest,
  StudioPreviewRequest,
  StudioPreviewResponse,
  StudioShareRequest,
  StudioTemplateResponse,
  StudioVersionListResponse,
} from "@bi/shared/types";

export class ApiError extends Error {
  readonly status: number | "network";
  readonly detail: string;
  /** The response's `detail` when it was an OBJECT rather than a string (`detail`
   *  above then carries its `message`). The studio's stale-save 409 answers
   *  `{error, message, current_version}`, and the canvas needs `current_version`
   *  to say which version is now stored before it offers to reload. */
  readonly payload: Record<string, unknown> | null;
  constructor(
    status: number | "network",
    detail: string,
    payload: Record<string, unknown> | null = null,
  ) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.payload = payload;
  }
  /** Signed in but not allowed: not an employee, or no `bi_site` grant at the route's level. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
  /** Not signed in. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

async function send(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (EMPLOYEE_EMAIL && !headers.has("x-employee-email")) {
    headers.set("x-employee-email", EMPLOYEE_EMAIL);
  }

  let resp: Response;
  try {
    resp = await fetch(`${BI_API_BASE}${path}`, {
      ...init,
      headers,
      credentials: "include",
    });
  } catch (e) {
    throw new ApiError("network", (e as Error).message || "Network request failed.");
  }

  if (!resp.ok) {
    let detail = `Request failed (${resp.status})`;
    let payload: Record<string, unknown> | null = null;
    try {
      const body = (await resp.json()) as { detail?: unknown };
      if (body && typeof body.detail === "string") detail = body.detail;
      else if (body && typeof body.detail === "object" && body.detail !== null) {
        payload = body.detail as Record<string, unknown>;
        if (typeof payload.message === "string") detail = payload.message;
      }
    } catch {
      // Non-JSON error body — keep the generic detail.
    }
    throw new ApiError(resp.status, detail, payload);
  }
  return resp;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const resp = await send(path, init);
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

function jsonBody(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };
}

// --- Identity -----------------------------------------------------------------
// The boot identity probe is the catalog fetch (GET /dashboards): 401/403 branch
// the same way a /me call would. `fetchTaggingIdentity` (GET /api/bi/tagging/me)
// answers the email and write level, not the boot probe.

// --- Dashboards ---------------------------------------------------------------

/** Also the boot identity probe: a success proves a live `bi_authority` session,
 *  so it clears the signed-out flag and caches `employeeEmail` inline (not in a
 *  TanStack `onSuccess`, removed from `useQuery` in v5). The SPA cannot read the
 *  HttpOnly cookie, so this email is how it knows its `private-bi-{email}`
 *  channel. THIS load must read it off the response: the cache only makes the
 *  NEXT load correct from its first render. */
// THE THREE CATALOG ROUTES carry `?catalog=` when a per-PR preview is open
// (lib/preview-catalog.ts). Listing, opening and querying a dashboard must all
// name the SAME catalog or a preview would list its own tiles and then query
// the merged definitions of them — so the selector is appended here, once per
// route, rather than at each call site.
export async function fetchDashboards(): Promise<BiDashboardListResponse> {
  const data = await request<BiDashboardListResponse>(
    withCatalog("/api/bi/dashboards", activePreviewCatalog()),
  );
  clearSignedOut();
  if (data.employeeEmail) storeEmployeeEmail(data.employeeEmail);
  return data;
}

export function fetchDashboard(id: string): Promise<BiDashboardDetailResponse> {
  return request<BiDashboardDetailResponse>(
    withCatalog(`/api/bi/dashboards/${encodeURIComponent(id)}`, activePreviewCatalog()),
  );
}

export function queryDashboardCharts(
  dashboardId: string,
  body: BiQueryRequest,
): Promise<BiQueryResponse> {
  return request<BiQueryResponse>(
    withCatalog(
      `/api/bi/dashboards/${encodeURIComponent(dashboardId)}/query`,
      activePreviewCatalog(),
    ),
    jsonBody(body),
  );
}

export function fetchChartResult(resultKey: string): Promise<BiChartResultResponse> {
  return request<BiChartResultResponse>(`/api/bi/results/${encodeURIComponent(resultKey)}`);
}

// --- Ask Mira BI ---------------------------------------------------------------
// `bi_site` at read, not full (`require_bi_employee_ask`) — see the route's own
// docstring in `bi/web/routes/bi_ask.py`. Both ways of asking ENQUEUE and answer
// 202 with a thread id and the position of the pending assistant message: the
// model loop and every BigQuery scan happen in the worker, never on the web
// request (doctrine §1.1). The answer arrives on the `bi.ask.settled` push, or
// on a `fetchAskThread` read for a client that was not connected when it
// settled. A business-level failure (no source, a compiler rejection, a dry-run
// refusal) is `status: "error"` ON THE MESSAGE, never an HTTP error, so it never
// reaches `ApiError` at all.

export function startAskThread(body: AskRequest): Promise<AskAccepted> {
  return request<AskAccepted>("/api/bi/ask/threads", jsonBody(body));
}

export function askFollowUp(threadId: string, body: AskRequest): Promise<AskAccepted> {
  return request<AskAccepted>(
    `/api/bi/ask/threads/${encodeURIComponent(threadId)}/messages`,
    jsonBody(body),
  );
}

export function fetchAskThreads(): Promise<AskThreadList> {
  return request<AskThreadList>("/api/bi/ask/threads");
}

export function fetchAskThread(threadId: string): Promise<AskThreadView> {
  return request<AskThreadView>(`/api/bi/ask/threads/${encodeURIComponent(threadId)}`);
}

export function deleteAskThread(threadId: string): Promise<void> {
  return request<void>(`/api/bi/ask/threads/${encodeURIComponent(threadId)}`, {
    method: "DELETE",
  });
}

export function askFeedback(body: AskFeedbackRequest): Promise<AskFeedbackAck> {
  return request<AskFeedbackAck>("/api/bi/ask/feedback", jsonBody(body));
}

/** Resync the catalog now rather than waiting for the five-minute tick.
 *  Needs `bi_site` at `read` only. */
export function triggerBiSync(): Promise<BiSyncAck> {
  return request<BiSyncAck>("/api/bi/sync", { method: "POST" });
}

// --- Pusher auth --------------------------------------------------------------

export function biPusherAuth(channelName: string, socketId: string): Promise<PusherAuthResponse> {
  const body = new URLSearchParams({ channel_name: channelName, socket_id: socketId });
  return request<PusherAuthResponse>("/api/bi/pusher/auth", {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
}

// --- Tagging console ----------------------------------------------------------
//
// The tagging system's whole operator surface lives on this app (backend:
// `web/routes/bi_tagging.py`). Reads take `bi_site` at `read`; every write and
// every run takes it at `full` — a run spends real model money, so the console
// asks `fetchTaggingIdentity` what the caller holds and declines to draw the
// buttons it would only be refused. The refusal is still the backend's.
//
// Nothing below names a tagger: the roster comes from the backend registry, so
// a tagger added there appears here with no change to this file.

export function fetchTaggingIdentity(): Promise<TaggingIdentity> {
  return request<TaggingIdentity>("/api/bi/tagging/me");
}

export function fetchTaggers(): Promise<TaggerInfo[]> {
  return request<TaggerInfo[]>("/api/bi/tagging/taggers");
}

export function fetchTags(tagger: string): Promise<TagDef[]> {
  return request<TagDef[]>(`/api/bi/tagging/taggers/${encodeURIComponent(tagger)}/tags`);
}

export function createTag(tagger: string, body: TagDefCreate): Promise<TagDef> {
  return request<TagDef>(
    `/api/bi/tagging/taggers/${encodeURIComponent(tagger)}/tags`,
    jsonBody(body),
  );
}

export function updateTag(tagId: string, body: TagDefUpdate): Promise<TagDef> {
  return request<TagDef>(`/api/bi/tagging/tags/${encodeURIComponent(tagId)}`, {
    ...jsonBody(body),
    method: "PATCH",
  });
}

export function exportTagset(tagger: string): Promise<TagSet> {
  return request<TagSet>(`/api/bi/tagging/taggers/${encodeURIComponent(tagger)}/export`);
}

/** Apply an exported tag set. `dryRun` reports what WOULD change and writes
 *  nothing — the console always calls that first and shows the diff before an
 *  operator confirms. */
export function importTagset(tagger: string, body: TagSetImport): Promise<TagSetDiff> {
  return request<TagSetDiff>(
    `/api/bi/tagging/taggers/${encodeURIComponent(tagger)}/import`,
    jsonBody(body),
  );
}

export function fetchTagRuns(tagger?: string): Promise<TagRunRow[]> {
  const qs = tagger ? `?tagger=${encodeURIComponent(tagger)}` : "";
  return request<TagRunRow[]>(`/api/bi/tagging/runs${qs}`);
}

export function fetchTagRun(runId: string): Promise<TagRunRow> {
  return request<TagRunRow>(`/api/bi/tagging/runs/${encodeURIComponent(runId)}`);
}

export function fetchRunCounts(runId: string): Promise<TagCount[]> {
  return request<TagCount[]>(`/api/bi/tagging/runs/${encodeURIComponent(runId)}/counts`);
}

export function fetchRunFindings(runId: string): Promise<TagFinding[]> {
  return request<TagFinding[]>(`/api/bi/tagging/runs/${encodeURIComponent(runId)}/findings`);
}

/** The DEBUG half of a run: per subject, the prompt it was sent, the transcript
 *  it was sent, and the model's reply verbatim — including for a subject that
 *  produced no finding at all. Empty for a run from before recruiter migration
 *  0192, and for one whose inputs have passed their 14-day expiry. */
export async function fetchRunInputs(runId: string): Promise<TagRunInput[]> {
  // Paged: every row repeats the system prompt plus a transcript, and a whole
  // 2,000-subject run in one response is past Cloud Run's 32 MiB cap (the
  // debug download 500'd on 2026-09-08). A page shorter than the limit is the
  // last one.
  const limit = 200;
  const all: TagRunInput[] = [];
  for (let offset = 0; ; offset += limit) {
    const page = await request<TagRunInput[]>(
      `/api/bi/tagging/runs/${encodeURIComponent(runId)}/inputs?offset=${offset}&limit=${limit}`,
    );
    all.push(...page);
    if (page.length < limit) return all;
  }
}

export function startTagRun(tagger: string, body: TagRunRequest): Promise<TagRunAck> {
  return request<TagRunAck>(
    `/api/bi/tagging/taggers/${encodeURIComponent(tagger)}/runs`,
    jsonBody(body),
  );
}

export function fetchProjectTags(projectId: string): Promise<ProjectTag[]> {
  return request<ProjectTag[]>(`/api/bi/tagging/projects/${encodeURIComponent(projectId)}/tags`);
}

// --- The playground: online, drafts, compare ---------------------------------
//
// ONLINE is the schema file committed in recruiter and deployed with it; the
// backend mirrors it on a tick. A draft is forked from it, edited, run offline,
// compared, and DOWNLOADED so an engineer commits it. Nothing here can change
// production; the console only notices when the deploy lands.

/** The schema this tagger asks in production right now. 404 until the first
 *  ingest, which the page renders as "nothing online yet" rather than an error. */
export function fetchOnlineSchema(tagger: string): Promise<OnlineSchemaInfo> {
  return request<OnlineSchemaInfo>(
    `/api/bi/tagging/taggers/${encodeURIComponent(tagger)}/online`,
  );
}

export function fetchDrafts(tagger: string): Promise<TagDraft[]> {
  return request<TagDraft[]>(`/api/bi/tagging/taggers/${encodeURIComponent(tagger)}/drafts`);
}

export function createDraft(tagger: string, body: TagDraftCreate): Promise<TagDraft> {
  return request<TagDraft>(
    `/api/bi/tagging/taggers/${encodeURIComponent(tagger)}/drafts`,
    jsonBody(body),
  );
}

export function updateDraft(draftId: string, body: TagDraftUpdate): Promise<TagDraft> {
  return request<TagDraft>(`/api/bi/tagging/drafts/${encodeURIComponent(draftId)}`, {
    ...jsonBody(body),
    method: "PATCH",
  });
}

export function deleteDraft(draftId: string): Promise<void> {
  return request<void>(`/api/bi/tagging/drafts/${encodeURIComponent(draftId)}`, {
    method: "DELETE",
  });
}

export function fetchDraftDiff(draftId: string): Promise<SchemaDiff> {
  return request<SchemaDiff>(`/api/bi/tagging/drafts/${encodeURIComponent(draftId)}/diff`);
}

/** The draft's JSON as the file an engineer commits, named `<tagger>.json`
 *  by the backend. Fetched as text so the bytes saved are the bytes served. */
export async function downloadDraft(draftId: string): Promise<string> {
  const resp = await send(`/api/bi/tagging/drafts/${encodeURIComponent(draftId)}/download`);
  return resp.text();
}

/** Two runs of one tagger joined on (subject, tag). Counts are over the whole
 *  join; `rows` is one page of it. */
export function fetchRunCompare(
  runA: string,
  runB: string,
  opts: { status?: string | null; offset?: number; limit?: number } = {},
): Promise<RunCompare> {
  const qs = new URLSearchParams();
  if (opts.status) qs.set("status", opts.status);
  if (opts.offset) qs.set("offset", String(opts.offset));
  if (opts.limit) qs.set("limit", String(opts.limit));
  const tail = qs.toString() ? `?${qs.toString()}` : "";
  return request<RunCompare>(
    `/api/bi/tagging/runs/${encodeURIComponent(runA)}/compare/${encodeURIComponent(runB)}${tail}`,
  );
}

// --- The corpus: conversations uploaded from another environment --------------

/** ONE prompt a tagger asks, and the response shape that call sends with it.
 *
 *  Both halves matter. Since the funnel scorers, the offer judge, the file judge
 *  and the message triage moved to strict structured outputs, their per-field
 *  rules live in `responseSchema` and not in the prompt, so a reader shown only
 *  the prompt is shown less than the model gets. `responseSchema` is null for a
 *  call that still asks in prose alone. */
export interface TaggerPromptBlock {
  key: string;
  source: string;
  prompt: string;
  responseSchema: Record<string, unknown> | null;
  tagIds: string[];
}

/** The committed schema a tagger asks, plus the prompt each of its blocks
 *  assembles to. Download it, edit it, hand it back to a RUN to try the change;
 *  committing it to `recruiter/tagging/schema/` is what makes it live.
 *
 *  `prompt` is the FIRST block's and is kept only for the single-block case. A
 *  tagger that asks two questions at two moments has two blocks, and rendering
 *  `prompt` for one of those would show a prompt no call ever sends. */
export function fetchTaggerSchema(tagger: string): Promise<{
  schema: Record<string, unknown>;
  prompt: string;
  blocks: TaggerPromptBlock[];
}> {
  return request<{
    schema: Record<string, unknown>;
    prompt: string;
    blocks: TaggerPromptBlock[];
  }>(`/api/bi/tagging/taggers/${encodeURIComponent(tagger)}/schema`);
}

export function fetchCorpusBatches(): Promise<CorpusBatch[]> {
  return request<CorpusBatch[]>("/api/bi/tagging/corpus");
}

/** One CHUNK of an upload. The caller splits a large file and passes the returned
 *  `batch.id` back as `batchId`, so five hundred projects become one corpus
 *  rather than five. */
export function uploadCorpus(body: CorpusUpload): Promise<CorpusUploadAck> {
  return request<CorpusUploadAck>("/api/bi/tagging/corpus", jsonBody(body));
}

export function deleteCorpus(batchId: string): Promise<void> {
  return request<void>(`/api/bi/tagging/corpus/${encodeURIComponent(batchId)}`, {
    method: "DELETE",
  });
}

/** Every published document this environment serves. A document is not a
 *  dashboard: it is one self-contained page, served whole and authorized by the
 *  same `bi_site` grant. Listed here so the catalog can show both without the
 *  reader needing to know which mechanism drew which page. */
export async function fetchArtifacts(): Promise<ArtifactSummary[]> {
  return request<ArtifactSummary[]>("/api/bi/artifacts");
}

// --- The studio: sources and the dashboards people build over them -----------
//
// Every route below sits under the SAME `/api/bi` prefix and the same `bi_site`
// grant as the authored dashboard routes above; the studio is an editor
// attached to this app, not a second product. The read/write split is the
// backend's: `GET /sources` and `GET /mine/dashboards` take `bi_site` at
// `read`, while every write AND every query takes it at `full` — a built query
// spends real BigQuery money on a shape nobody reviewed. Nothing here asks
// which the caller holds; a 403 from a write is the answer, and the pages
// render it rather than pre-empting it.

export function fetchBiSources(): Promise<BiSourceListResponse> {
  return request<BiSourceListResponse>("/api/bi/sources");
}

export function fetchBiSource(sourceId: string): Promise<BiSourceDetailResponse> {
  return request<BiSourceDetailResponse>(`/api/bi/sources/${encodeURIComponent(sourceId)}`);
}

/** A select control's options. `status: "pending"` with an empty list means NOT
 *  KNOWN YET (the read enqueued a job) — never "no options". The caller must
 *  render the two differently, which is why the whole response comes back
 *  rather than just the values. */
export function fetchBiSourceValues(
  sourceId: string,
  column: string,
): Promise<BiSourceValuesResponse> {
  return request<BiSourceValuesResponse>(
    `/api/bi/sources/${encodeURIComponent(sourceId)}/values/${encodeURIComponent(column)}`,
  );
}

/** The experiment register. `status: "pending"` means one or more picker
 *  columns are still being computed and the list is what is known so far —
 *  never "there are no experiments", the same distinction
 *  `fetchBiSourceValues` draws. */
export function fetchExperiments(): Promise<ExperimentListResponse> {
  return request<ExperimentListResponse>("/api/bi/experiments");
}

/** One search across sources, columns, dashboards and experiments. */
export function searchBi(query: string): Promise<BiSearchResponse> {
  return request<BiSearchResponse>(`/api/bi/search?q=${encodeURIComponent(query)}`);
}

export function fetchStudioDashboards(): Promise<StudioDashboardListResponse> {
  return request<StudioDashboardListResponse>("/api/bi/mine/dashboards");
}

export function fetchStudioDashboard(id: string): Promise<StudioDashboardResponse> {
  return request<StudioDashboardResponse>(`/api/bi/mine/dashboards/${encodeURIComponent(id)}`);
}

export function createStudioDashboard(
  body: StudioDashboardCreate,
): Promise<StudioDashboardResponse> {
  return request<StudioDashboardResponse>("/api/bi/mine/dashboards", jsonBody(body));
}

/** A save. `body.version` is the version the editor LOADED, and a mismatch
 *  comes back as a 409 whose `ApiError.payload.current_version` names what is
 *  now stored. NEVER retry it with a bumped version — that is last-write-wins
 *  wearing a check, and it is the one thing standing between two people editing
 *  a shared dashboard and one of them losing an afternoon. */
export function updateStudioDashboard(
  id: string,
  body: StudioDashboardUpdate,
): Promise<StudioDashboardResponse> {
  return request<StudioDashboardResponse>(`/api/bi/mine/dashboards/${encodeURIComponent(id)}`, {
    ...jsonBody(body),
    method: "PUT",
  });
}

export function deleteStudioDashboard(id: string): Promise<void> {
  return request<void>(`/api/bi/mine/dashboards/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/** Copy a dashboard the caller can READ into one they OWN. The copy is always
 *  private, whatever the original's visibility. */
export function forkStudioDashboard(
  id: string,
  body: StudioForkRequest = {},
): Promise<StudioDashboardResponse> {
  return request<StudioDashboardResponse>(
    `/api/bi/mine/dashboards/${encodeURIComponent(id)}/fork`,
    jsonBody(body),
  );
}

export function shareStudioDashboard(
  id: string,
  body: StudioShareRequest,
): Promise<StudioDashboardResponse> {
  return request<StudioDashboardResponse>(
    `/api/bi/mine/dashboards/${encodeURIComponent(id)}/share`,
    jsonBody(body),
  );
}

export function fetchStudioVersions(id: string): Promise<StudioVersionListResponse> {
  return request<StudioVersionListResponse>(
    `/api/bi/mine/dashboards/${encodeURIComponent(id)}/versions`,
  );
}

/** Restore is a COPY FORWARD: the restored spec is saved as a NEW version on
 *  top of the current one, so the restore itself is undoable. */
export function restoreStudioVersion(id: string, version: number): Promise<StudioDashboardResponse> {
  return request<StudioDashboardResponse>(
    `/api/bi/mine/dashboards/${encodeURIComponent(id)}/versions/${version}/restore`,
    { method: "POST" },
  );
}

/** The dashboard as repo YAML, `{relative path: file text}`. Offered as TEXT to
 *  copy rather than as a download: the sandbox this app is viewed in blocks a
 *  page-initiated save, and a download link that silently does nothing is worse
 *  than a text area that works. */
export function exportStudioDashboard(id: string): Promise<StudioExportResponse> {
  return request<StudioExportResponse>(
    `/api/bi/mine/dashboards/${encodeURIComponent(id)}/export`,
    { method: "POST" },
  );
}

/** The studio twin of `queryDashboardCharts` — same request body, same
 *  `result_key`, same `bi.charts.settled` event. The differences are all
 *  server-side (the registry is assembled from the stored row, and every chart
 *  is validated and dry-run before a job exists), which is what lets the canvas
 *  and the dashboard page share one render path. */
export function queryStudioDashboardCharts(
  dashboardId: string,
  body: BiQueryRequest,
): Promise<BiQueryResponse> {
  return request<BiQueryResponse>(
    `/api/bi/mine/dashboards/${encodeURIComponent(dashboardId)}/query`,
    jsonBody(body),
  );
}

/** A dashboard by SLUG, opened as a TEMPLATE.
 *
 *  AUTHORED FIRST, with a readable studio row as the fallback — the backend
 *  settles that, and `origin` says which was found. A 404 is a state this app
 *  renders ("no template with that slug here yet"), not a failure: the
 *  Experiment template is authored YAML that ships with the registry sync, and
 *  an environment that has not received it yet is a normal state on the way in.
 *
 *  Never assemble the query URL from `origin`: the response carries `queryPath`
 *  precisely so the routing rule does not exist in two places, where the symptom
 *  of them disagreeing is a 404 on the one dashboard everybody opens. */
export function fetchStudioTemplate(slug: string): Promise<StudioTemplateResponse> {
  return request<StudioTemplateResponse>(`/api/bi/templates/${encodeURIComponent(slug)}`);
}

/** Query a dashboard's charts at a path the SERVER named (a template's
 *  `queryPath`). The body and the response are the ordinary ones; only the route
 *  is late-bound, because an authored template and a studio one answer on
 *  different ones and the client is not the thing that should decide which. */
export function queryChartsAtPath(path: string, body: BiQueryRequest): Promise<BiQueryResponse> {
  return request<BiQueryResponse>(path, jsonBody(body));
}

/** One transient chart, drawn while somebody is still composing it. The chart
 *  frequently does not exist anywhere yet, which is why the whole spec rides the
 *  request rather than a diff against a saved one. */
export function previewStudioChart(body: StudioPreviewRequest): Promise<StudioPreviewResponse> {
  return request<StudioPreviewResponse>("/api/bi/mine/preview", jsonBody(body));
}

// --- Permissions --------------------------------------------------------------
// The access list. EVERY route here needs `bi_site` at `full`, the read
// included: who can reach the warehouse is itself sensitive, and a `read` holder
// has no reason to enumerate their colleagues' access. So a 403 from these is
// the ordinary answer for most of the company, not a fault.

export function fetchBiGrants(): Promise<GrantListResponse> {
  return request<GrantListResponse>("/api/bi/permissions");
}

/** Grant `bi_site`, or change a holder's level. One call for both: the row is
 *  keyed `(email, area)` and the server upserts, so the page never has to know
 *  whether it is adding somebody or promoting them. */
export function setBiGrant(body: GrantRequest): Promise<GrantAck> {
  return request<GrantAck>("/api/bi/permissions", {
    ...jsonBody(body),
    method: "PUT",
  });
}

export function revokeBiGrant(email: string): Promise<GrantAck> {
  return request<GrantAck>(`/api/bi/permissions/${encodeURIComponent(email)}`, {
    method: "DELETE",
  });
}
